/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Testimonials section - What Users Say About AI Removal
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'Excellent Tool for Social Campaigns',
    content:
      "With FalcoCut, I no longer spend hours re-recording intros or corrections. The cloned voice sounds just like me, and my audience can't tell the difference. It saves me time and keeps my podcast consistent.",
    role: 'Social Media Marketer',
    name: 'Marcus Lee',
  },
  {
    title: 'Helped a Lot on My Online Courses',
    content:
      'My students expect clear, distraction‑free lessons. FalcoCut helps me update course videos with fresh translations and accurate visuals, so learning stays smooth across regions.',
    role: 'Online Educator',
    name: 'Priya Sharma',
  },
  {
    title: 'Perfect for Marketing Teams',
    content:
      'We often repurpose old footage for new campaigns. FalcoCut lets us refresh assets without losing quality, making localization and compliance much easier.',
    role: 'Marketing Director',
    name: 'Daniel Chen',
  },
  {
    title: 'Speed up My Freelance Projects',
    content:
      "Clients want polished videos on tight deadlines. FalcoCut's speed and accuracy mean I can deliver professional edits without spending hours fixing frames manually.",
    role: 'Freelance Video Editor',
    name: 'Sofia Martinez',
  },
  {
    title: 'Very Simple to Use',
    content:
      "I'm not a professional editor, but FalcoCut makes my personal projects look clean and shareable. It's easy to use and gives me results that feel professional.",
    role: 'Student',
    name: 'Alex Johnson',
  },
  {
    title: 'Makes Internal Training Smooth',
    content:
      'As an HR Manager supporting multilingual teams, I need training videos that stay clear and easy for everyone to follow. FalcoCut lets me update materials quickly, keeping subtitles accurate and visuals consistent for all employees.',
    role: 'YouTube Creator',
    name: 'Diego Morales',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{role}</p>
        <p className="text-gray-500 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          What Our Customers Say
        </h2>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
